import type { Schemas } from "@artisan/api-client";
import { Badge, Button, Card, cn, formatMNT, Input } from "@artisan/ui";
import { Armchair, Check, CookingPot, Droplets, Layers, Package, Plus, Search, Square, Wrench } from "lucide-react";
import { useEffect, useState } from "react";

import { api, describeError, unwrap } from "../lib/api";
import { unitLabel } from "../project/model";

export type Hit = Schemas["SearchHit"];
type Category = Schemas["CategoryOut"];

const ICONS: Record<string, typeof Package> = {
  board: Layers,
  front: Square,
  worktop: Layers,
  hardware: Wrench,
  fixture: Droplets,
  appliance: CookingPot,
  furniture: Armchair,
};

export function hitIcon(renderClass: string | null | undefined) {
  return (renderClass && ICONS[renderClass]) || Package;
}

/** Search the supplier catalog (and the organization's own products). */
export function CatalogBrowser({
  onPick,
  picked = [],
  columns = 3,
}: {
  onPick?: (hit: Hit) => void;
  picked?: string[];
  columns?: 2 | 3 | 4;
}) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [cats, setCats] = useState<Category[]>([]);
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    unwrap(api().GET("/v1/catalog/categories"))
      .then((all) => setCats(all.filter((c) => c.parent_id === null)))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      unwrap(api().GET("/v1/catalog/search", { params: { query: { q: q.trim(), limit: 30, ...(category ? { category } : {}) } } }))
        .then((page) => {
          if (!live) return;
          setHits(page.items);
          setError(null);
        })
        .catch((e) => live && setError(describeError(e)));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q, category]);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3.5 size-5 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ЛДСП 18мм, царс, угаалтуур, бариул…" className="h-12 pl-11 text-[16px]" aria-label="Каталогоос хайх" />
      </div>
      {cats.length ? (
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Ангилал">
          {[{ slug: null, name_mn: "Бүгд", render_class: null }, ...cats].map((c) => {
            const Icon = c.slug ? hitIcon(c.render_class) : null;
            return (
              <button
                key={c.slug ?? "all"}
                type="button"
                role="radio"
                aria-checked={category === c.slug}
                onClick={() => setCategory(c.slug)}
                className={cn(
                  "flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-medium",
                  category === c.slug ? "border-foreground bg-foreground text-background" : "bg-card hover:bg-accent",
                )}
              >
                {Icon ? <Icon className="size-4" aria-hidden /> : null}
                {c.name_mn}
              </button>
            );
          })}
        </div>
      ) : null}
      {error ? <p className="text-sm text-muted-foreground">{error}</p> : null}
      {hits && hits.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Илэрц алга. Өөр үгээр хайна уу.</p> : null}
      <div className={cn("grid gap-4", columns === 2 ? "grid-cols-2" : columns === 4 ? "grid-cols-4" : "grid-cols-3")}>
        {(hits ?? []).map((h) => {
          const Icon = hitIcon(h.category.render_class);
          const isPicked = picked.includes(h.id);
          const dims = [h.width_mm, h.height_mm, h.depth_mm].filter((v): v is number => typeof v === "number" && v > 0);
          return (
            <Card key={h.id} className={cn("flex flex-col overflow-hidden p-0", isPicked && "border-foreground ring-1 ring-foreground")}>
              <div className="relative grid h-28 place-items-center bg-muted">
                <Icon className="size-10 text-muted-foreground/60" aria-hidden strokeWidth={1.2} />
                {isPicked ? (
                  <Badge variant="default" icon={Check} className="absolute top-2 left-2">
                    Нэмсэн
                  </Badge>
                ) : null}
                {h.own ? (
                  <Badge variant="soft" className="absolute top-2 right-2">
                    Өөрийн
                  </Badge>
                ) : null}
              </div>
              <div className="flex flex-1 flex-col gap-1 p-3.5">
                <p className="line-clamp-2 font-semibold leading-snug">{h.name_mn}</p>
                <p className="truncate font-mono text-xs text-muted-foreground">
                  {h.sku}
                  {dims.length ? ` · ${dims.join("×")} мм` : ""}
                </p>
                <p className="text-xs text-muted-foreground">
                  {h.category.name_mn}
                  {h.offer_count ? ` · ${h.offer_count} нийлүүлэгч` : ""}
                </p>
                <div className="mt-auto flex items-center justify-between pt-2">
                  <span className="text-[16px] font-semibold tabular">
                    {h.min_price_mnt !== null ? formatMNT(h.min_price_mnt) : "Үнэ асуух"}
                    {h.min_price_mnt !== null && h.unit ? <span className="text-xs font-normal text-muted-foreground"> / {unitLabel(h.unit)}</span> : null}
                  </span>
                  {onPick ? (
                    <Button
                      variant={isPicked ? "default" : "outline"}
                      size="icon"
                      aria-label={isPicked ? `${h.name_mn} сонгосон` : `${h.name_mn} нэмэх`}
                      onClick={() => onPick(h)}
                    >
                      {isPicked ? <Check aria-hidden /> : <Plus aria-hidden />}
                    </Button>
                  ) : null}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
